import { createFileRoute } from "@tanstack/react-router";
import { Phone, MessageSquare, Mail, MapPin } from "lucide-react";
import { BUSINESS } from "@/lib/contact";
import { logContact } from "@/lib/log-contact";
import { BookingForm } from "@/components/BookingForm";
import { Toaster } from "@/components/ui/sonner";

export const Route = createFileRoute("/contact")({
  component: ContactPage,
  head: () => ({
    meta: [
      { title: "Contact StillFusion | Book Appliance Repair Sacramento" },
      {
        name: "description",
        content:
          "Call, text, or send a quick request to book same-day appliance repair in Sacramento and surrounding cities.",
      },
      { property: "og:title", content: "Contact StillFusion Appliance Repair" },
      { property: "og:description", content: "Same-day appointments in Sacramento, CA." },
      { property: "og:url", content: "https://stillfusionappliance.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://stillfusionappliance.com/contact" }],
  }),
});

function ContactPage() {
  return (
    <>
      <section className="mx-auto max-w-6xl px-5 py-16">
        <div className="relative isolate transform-gpu overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-primary/10 via-background to-accent/10 p-6 sm:p-10 [clip-path:inset(0_round_1.5rem)]">
          <div
            className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-primary/20 blur-3xl"
            aria-hidden
          />
          <div
            className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-accent/20 blur-3xl"
            aria-hidden
          />
          <div className="relative space-y-8">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-accent">
                Book a Visit
              </span>
              <h1 className="mt-4 text-4xl font-bold tracking-tight sm:text-5xl">
                Schedule your appliance repair
              </h1>
              <p className="mt-4 text-lg text-muted-foreground">
                Tell us what's broken — we'll confirm a same-day or next-day visit, usually within
                minutes.
              </p>
              <ul className="mt-6 space-y-2 text-sm text-muted-foreground">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Licensed & insured
                  technicians
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" /> Upfront, flat-rate pricing
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent" /> 90-day parts & labor
                  warranty
                </li>
              </ul>
            </div>
            <div className="w-full">
              <BookingForm />
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
            Contact
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            Get help in minutes
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-muted-foreground">
            Call, text, or send a quick request — we'll get back to you right away.
          </p>
        </div>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <a
            href={`tel:${BUSINESS.phone}`}
            onClick={() => {
              void logContact("call");
            }}
            className="surface-card flex items-center justify-between gap-3 p-5"
          >
            <span className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-primary text-primary-foreground">
                <Phone className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                  Call
                </span>
                <span className="block font-semibold">Call Now</span>
              </span>
            </span>
            <span className="text-sm font-semibold text-primary">Tap to call</span>
          </a>
          <a
            href={`sms:${BUSINESS.sms}`}
            onClick={() => {
              void logContact("text");
            }}
            className="surface-card flex items-center justify-between gap-3 p-5"
          >
            <span className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-accent text-accent-foreground">
                <MessageSquare className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                  Text
                </span>
                <span className="block font-semibold">Send a text</span>
              </span>
            </span>
            <span className="text-sm font-semibold text-accent">Tap to text</span>
          </a>
          <div className="surface-card flex items-center gap-3 p-5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-foreground">
              <Mail className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                Email
              </span>
              <span className="block font-semibold">{BUSINESS.email}</span>
            </span>
          </div>
          <div className="surface-card flex items-center gap-3 p-5">
            <span className="grid h-11 w-11 place-items-center rounded-xl bg-secondary text-foreground">
              <MapPin className="h-5 w-5" />
            </span>
            <span>
              <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                Service Area
              </span>
              <span className="block font-semibold">{BUSINESS.city} & 75mi around</span>
            </span>
          </div>
        </div>
      </section>
      <Toaster />
    </>
  );
}
